import { useEffect, useState, useCallback } from 'react';
import {
  Firestore,
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  deleteDoc,
  doc,
  updateDoc,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';

export interface Comment {
  comment_id: string;
  user_id: string;
  user_email: string;
  user_name?: string;
  message: string;
  created_at: Date;
  picture_id?: string;  // ID of the specific image this comment is for
  coordinates?: { 
    x: number;  // Relative position (0-1) on the image
    y: number;  // Relative position (0-1) on the image
  };
  reactions?: { [emoji: string]: string[] }; // emoji -> array of user_ids
  is_resolved?: boolean;
  resolved_at?: Date;
  resolved_by?: string;
  parent_comment_id?: string;  // ID of parent comment if this is a reply
  reply_count?: number;  // Number of replies (computed client-side)
}

export function useCommentsRealtime(
  db: Firestore,
  projectId: string | undefined,
  threadId: string | undefined
) {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId || !threadId) {
      setComments([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const commentsRef = collection(db, 'projects', projectId, 'threads', threadId, 'comments');
    const q = query(commentsRef, orderBy('created_at', 'asc'));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const commentsData: Comment[] = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            comment_id: doc.id,
            user_id: data.user_id,
            user_email: data.user_email,
            user_name: data.user_name,
            message: data.message,
            created_at: data.created_at instanceof Timestamp ? data.created_at.toDate() : new Date(),
            picture_id: data.picture_id,
            coordinates: data.coordinates,
            reactions: data.reactions || {},
            is_resolved: data.is_resolved || false,
            resolved_at: data.resolved_at instanceof Timestamp ? data.resolved_at.toDate() : undefined,
            resolved_by: data.resolved_by,
            parent_comment_id: data.parent_comment_id,
            reply_count: 0  // Will be computed below
          };
        });

        // Compute reply counts for parent comments
        const replyCounts = new Map<string, number>();
        commentsData.forEach(comment => {
          if (comment.parent_comment_id) {
            replyCounts.set(
              comment.parent_comment_id,
              (replyCounts.get(comment.parent_comment_id) || 0) + 1
            );
          }
        });

        // Apply reply counts
        commentsData.forEach(comment => {
          comment.reply_count = replyCounts.get(comment.comment_id) || 0;
        });

        setComments(commentsData);
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [db, projectId, threadId]);

  const addComment = useCallback(
    async (
      message: string,
      userId: string,
      userEmail: string,
      pictureId?: string,
      coordinates?: { x: number; y: number },  // Now expects relative coordinates (0-1)
      parentCommentId?: string  // Optional parent comment ID for replies
    ) => {
      if (!projectId || !threadId) return;

      const commentsRef = collection(db, 'projects', projectId, 'threads', threadId, 'comments');
      await addDoc(commentsRef, {
        user_id: userId,
        user_email: userEmail,
        user_name: userEmail.split('@')[0],
        message,
        created_at: serverTimestamp(),
        picture_id: pictureId || null,
        coordinates: coordinates || null,
        reactions: {},
        is_resolved: false,
        parent_comment_id: parentCommentId || null
      });
    },
    [db, projectId, threadId]
  );

  const deleteComment = useCallback(
    async (commentId: string) => {
      if (!projectId || !threadId) return;
      const commentRef = doc(db, 'projects', projectId, 'threads', threadId, 'comments', commentId);
      await deleteDoc(commentRef);
    },
    [db, projectId, threadId]
  );

  const toggleReaction = useCallback(
    async (commentId: string, emoji: string, userId: string) => {
      if (!projectId || !threadId) return;

      const comment = comments.find(c => c.comment_id === commentId);
      if (!comment) return;

      const reactions = { ...comment.reactions };
      if (!reactions[emoji]) {
        reactions[emoji] = [];
      }

      const userIndex = reactions[emoji].indexOf(userId);
      if (userIndex > -1) {
        reactions[emoji].splice(userIndex, 1);
        if (reactions[emoji].length === 0) {
          delete reactions[emoji];
        }
      } else {
        reactions[emoji].push(userId);
      }

      const commentRef = doc(db, 'projects', projectId, 'threads', threadId, 'comments', commentId);
      await updateDoc(commentRef, { reactions });
    },
    [db, projectId, threadId, comments]
  );

  const toggleResolve = useCallback(
    async (commentId: string, userId: string) => {
      if (!projectId || !threadId) return;

      const comment = comments.find(c => c.comment_id === commentId);
      if (!comment) return;

      const commentRef = doc(db, 'projects', projectId, 'threads', threadId, 'comments', commentId);
      
      if (comment.is_resolved) {
        // Unresolve
        await updateDoc(commentRef, {
          is_resolved: false,
          resolved_at: null,
          resolved_by: null
        });
      } else {
        // Resolve
        await updateDoc(commentRef, {
          is_resolved: true,
          resolved_at: serverTimestamp(),
          resolved_by: userId
        });
      }
    },
    [db, projectId, threadId, comments]
  );

  return { comments, loading, error, addComment, deleteComment, toggleReaction, toggleResolve };
}
